'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams, useRouter, useParams } from 'next/navigation';
import Image from 'next/image';
import {
  Container,
  Divider,
  Group,
  Input,
  Pagination,
  Tabs,
  Transition,
} from '@mantine/core';
import styles from './products.module.scss';
import VerticalCardList from '@/src/components/SliceMachine/VerticalCardList/VerticalCardList';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { fetchProducts } from '@/src/helpers/fetch-products';
import { Product } from '@/src/types/Header';
import FullScreenImageHalf from '@/src/components/SliceMachine/FullScreenImageHalf/FullScreenImageHalf';
import { IconX, IconSearch } from '@tabler/icons-react';
import { EditableSliceWrapper } from '@/src/components/SliceMachine/EditableSliceWrapper';

const ProductsPage = () => {
  const dictionary = useDictionary();
  const searchParams = useSearchParams();
  const router = useRouter();
  const params = useParams();

  const initialSearch = searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const result: any = await fetchProducts(page, searchQuery);
      setProducts(result.products);
      setTotalPages(result.totalPages);
    };
    fetchData();
  }, [page, searchQuery]);

  const updateSearchParam = (value: string) => {
    setSearchQuery(value);
    const newParams = new URLSearchParams(window.location.search);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    router.replace(`?${newParams.toString()}`);
  };

  return (
    <>
      <EditableSliceWrapper
        slice={dictionary.pages.products.slices[0]}
        sliceIndex={0}
        lang={params.lang as 'fr' | 'en'}
        pageKey="articles" >
        <FullScreenImageHalf data={dictionary.pages.products.slices[0]} />
      </EditableSliceWrapper >
      <Container my="lg" className={styles.wrapper}>
        <Tabs
          value={
            dictionary.pages.products.periodOptions.find(
              (p: { value: string; label: string }) => p.value === searchQuery
            )?.value || ''
          }
          onChange={(value) => {
            updateSearchParam(value || '');
            setPage(1);
          }}
          mb="md"
          radius="md"
          classNames={{ tab: styles.tab }}
        >
          <Group justify="space-between" align="center" mb="sm">
            <Tabs.List grow>
              {dictionary.pages.products.periodOptions.map(
                (period: { value: string; label: string }) => (
                  <Tabs.Tab key={period.value} value={period.value}>
                    {period.label}
                  </Tabs.Tab>
                )
              )}
            </Tabs.List>

            <Group ml="md" gap="xs">
              <IconSearch
                style={{ cursor: 'pointer' }}
                onClick={() => setShowSearch((prev) => !prev)}
                title="Toggle search"
              />
              {searchQuery && (
                <IconX
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    updateSearchParam('');
                    setPage(1);
                  }}
                  title="Clear filters"
                />
              )}
            </Group>
          </Group>
        </Tabs>

        {showSearch && (
          <Group align="center" mb="lg" gap="xs">
            <Image
              src="https://img.icons8.com/c9a456/ios-filled/50/search--v1.png"
              alt="search"
              height={20}
              width={20}
            />
            <Input
              placeholder={dictionary.pages.products.search}
              value={searchQuery}
              onChange={(event) => updateSearchParam(event.target.value)}
              style={{ flex: 1 }}
            />
          </Group>
        )}

        {/* @ts-ignore */}
        <VerticalCardList products={products} data={dictionary.pages.products} />

        <Group mt="xl" pt="lg" justify="center">
          <Pagination
            // @ts-ignore
            page={page}
            onChange={setPage}
            total={totalPages}
            color="black"
          />
        </Group>
      </Container>
    </>
  );
};

export default ProductsPage;
